import { createElement, useContext, useMemo } from 'react';
import { MessageContext, defaultValue } from './message-context';
import { MessageError, errorMessages } from './message-error';
function getOnError(parent, pathSep, onError, debug) {
    const asId = (path) => path.join(pathSep || ',');
    function msgError(path, code) {
        throw new MessageError(path, code, asId);
    }
    function msgWarning(path, code) {
        console.warn(errorMessages[code], path);
        return asId(path);
    }
    if (onError === undefined) {
        // debug is deprecated, will be removed later
        if (typeof debug === 'function')
            return (path, code) => debug(`${errorMessages[code]}: ${asId(path)}`);
        onError = debug;
    }
    switch (onError) {
        case 'silent':
            return asId;
        case 'error':
            return msgError;
        case 'warn':
            return msgWarning;
        default:
            if (typeof onError === 'function') {
                const _onError = onError;
                return (path, code) => _onError(new MessageError(path, code, asId));
            }
            return parent.onError || msgWarning;
    }
}
function getLocales({ locales }, locale) {
    const fallback = locales.filter(fb => fb !== locale);
    return [locale].concat(fallback);
}
function getMessages({ merge, messages }, locale, lcMessages) {
    const res = Object.assign({}, messages);
    const prev = res[locale];
    res[locale] =
        prev && typeof prev === 'object' ? merge({}, prev, lcMessages) : lcMessages;
    return res;
}
function getPathSep(context, pathSep) {
    return pathSep === null || typeof pathSep === 'string'
        ? pathSep
        : context.pathSep;
}
/**
 * `<MessageProvider messages [locale] [merge] [onError] [pathSep]>`
 *
 * Makes the messages available for its descendants via a React Context.
 * To support multiple locales and/or namespaces, MessageProviders may be used within each other, merging each provider's messages with those of its parents.
 * The locale preference order is also set similarly, from nearest to furthest.
 *
 * @public
 *
 * @example
 * ```js
 * import React from 'react'
 * import { Message, MessageProvider } from '@messageformat/react'
 *
 * const messages = { example: { key: 'Your message here' } }
 * const extended = { other: { key: 'Another message' } }
 *
 * const Example = () => (
 *   <span>
 *     <Message id={['example', 'key']} />
 *     {' | '}
 *     <Message id="other/key" />
 *   </span>
 * ) // 'Your message here | Another message'
 *
 * export const App = () => (
 *   <MessageProvider messages={messages} pathSep="/">
 *     <MessageProvider messages={extended}>
 *       <Example />
 *     </MessageProvider>
 *   </MessageProvider>
 * )
 * ```
 */
export function MessageProvider(props) {
    const { children, context: propContext, debug, locale = '', merge, messages, onError, pathSep } = props;
    let parent = useContext(MessageContext);
    if (propContext)
        parent = propContext;
    else if (propContext === null)
        parent = defaultValue;
    const value = useMemo(() => {
        const ps = getPathSep(parent, pathSep);
        return {
            locales: getLocales(parent, locale),
            merge: merge || parent.merge,
            messages: getMessages(parent, locale, messages),
            onError: getOnError(parent, ps, onError, debug),
            pathSep: ps
        };
    }, [parent, locale, merge, messages, pathSep]);
    return createElement(MessageContext.Provider, { value }, children);
}
